import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Empty,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, ThunderboltOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchData } from "../../api/apiService";
import { NOTICES } from "../../api/endpointMap";

const { Title, Paragraph, Text } = Typography;

// Map portal segment → detail endpoint factory.
//   - GST + TDS expose `GET /{portal}/notices/{record_id}` directly.
//   - Income tax splits notices into e-proceedings + demands; the list
//     endpoint returns rows with `notice_type` like "E Proceeding" /
//     "Demand". We'll try `/e-proceedings/{id}` first (the dominant
//     case) and fall back to the `/notices` list result if that 404s.
//
// Keeping this fragile-but-explicit instead of inventing a unified
// /notices/{id} endpoint — that's a backend refactor outside Module 11.
const PORTAL_CONFIG = {
  "income-tax": {
    label: "Income Tax",
    listEndpoint: NOTICES.INCOME_TAX_LIST,
    detailEndpoint: (id) => `/api/v1/income-tax/e-proceedings/${id}`,
    fallbackDetailEndpoint: (id) => `/api/v1/income-tax/demands/${id}`,
  },
  gst: {
    label: "GST",
    listEndpoint: NOTICES.GST_LIST,
    detailEndpoint: (id) => `/api/v1/gst/notices/${id}`,
  },
  tds: {
    label: "TDS",
    listEndpoint: NOTICES.TDS_LIST,
    detailEndpoint: (id) => `/api/v1/tds/notices/${id}`,
  },
};

const fmtDate = (s) => {
  if (!s) return "-";
  const d = dayjs(s);
  return d.isValid() ? d.format("DD MMM YYYY") : s;
};

const STATUS_COLOR = {
  OPEN: "orange",
  PENDING: "orange",
  RESPONDED: "green",
  CLOSED: "default",
  EXPIRED: "red",
};

// Spec uses "gst" as the portal segment; the tenant route tree (mirrored
// under /embed/*) uses "gstin". Map back when building list URLs so the
// Back/Breadcrumb links land on the actual mounted route, not the catchall.
const portalToTenant = (p) => (p === "gst" ? "gstin" : p);

// Tolerant unwrap that handles both detail-shape `{success, data: {...}}`
// and list-row pulls where we have the row directly.
const unwrapDetail = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  if (raw.data && typeof raw.data === "object") {
    // Detail endpoints return {success, message, data: <row>} OR
    // {success, message, data: {records: [<row>]}} — be flexible.
    if (Array.isArray(raw.data.records)) return raw.data.records[0] || null;
    return raw.data;
  }
  if (raw.result && typeof raw.result === "object") return raw.result;
  return raw;
};

const EmbedNoticeDetail = () => {
  const { portal, noticeId } = useParams();
  const config = PORTAL_CONFIG[portal];

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    if (!config || !noticeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUsedFallback(false);

    const tryDetail = async (factory) => {
      const raw = await fetchData(factory(noticeId));
      return unwrapDetail(raw);
    };

    (async () => {
      try {
        const detail = await tryDetail(config.detailEndpoint);
        if (cancelled) return;
        if (detail) {
          setRow(detail);
          return;
        }
        if (config.fallbackDetailEndpoint) {
          try {
            const fb = await tryDetail(config.fallbackDetailEndpoint);
            if (cancelled) return;
            if (fb) {
              setRow(fb);
              setUsedFallback(true);
              return;
            }
          } catch {
            // primary 200'd with empty body and fallback also failed
          }
        }
        setError("Notice not found");
      } catch (primaryErr) {
        // Primary 4xx/5xx. Try fallback if income-tax — record may be a
        // demand instead of an e-proceeding.
        if (config.fallbackDetailEndpoint) {
          try {
            const fb = await tryDetail(config.fallbackDetailEndpoint);
            if (cancelled) return;
            if (fb) {
              setRow(fb);
              setUsedFallback(true);
              return;
            }
          } catch {
            // Both failed; surface the primary error.
          }
        }
        if (cancelled) return;
        setError(
          primaryErr?.detail ||
            primaryErr?.message ||
            "Failed to load notice"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config, noticeId]);

  if (!config) return <Navigate to="/embed/dashboard" replace />;

  if (loading && !row) {
    return (
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error && !row) {
    return (
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={error}
        >
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            href={`/embed/${portalToTenant(portal)}/notices`}
          >
            Back to {config.label} notices
          </Button>
        </Empty>
      </div>
    );
  }

  const subject =
    row?.notice_name ||
    row?.subject ||
    row?.notice_type ||
    "Notice";
  const status = row?.notice_status;
  // Privacy: for partner clients the stored client_name is the partner
  // reference. Prefer an explicit partner_client_ref when present, then the
  // stored label, then the portal identifiers.
  const client =
    row?.partner_client_ref || row?.client_name || row?.gstin || row?.tan;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <Breadcrumb
        className="mb-3"
        items={[
          { title: <Link to="/embed/dashboard">Dashboard</Link> },
          {
            title: (
              <Link to={`/embed/${portalToTenant(portal)}/notices`}>
                {config.label} notices
              </Link>
            ),
          },
          { title: subject },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <Title level={4} className="!mb-1">
            {subject}
          </Title>
          <Space wrap>
            <Tag color="blue">{config.label}</Tag>
            {status && (
              <Tag color={STATUS_COLOR[status] || "default"}>{status}</Tag>
            )}
            {client && <Text type="secondary">{client}</Text>}
          </Space>
        </div>
        <Space>
          {/* AI response generation — income-tax only in v1 (the only
              portal where with_legal=true cleanly works today). The
              EmbedGenerateResponse page itself handles GST/TDS by
              rendering an "open in tenant app" empty state, but we
              don't even surface the button for them so partners aren't
              led to a dead-end. */}
          {portal === "income-tax" && (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              href={`/embed/generate-response/${noticeId}?portal=income-tax`}
            >
              Generate AI response
            </Button>
          )}
          <Button
            icon={<ArrowLeftOutlined />}
            href={`/embed/${portalToTenant(portal)}/notices`}
          >
            Back
          </Button>
        </Space>
      </div>

      {usedFallback && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="Loaded from secondary endpoint"
          description="This notice came from the demands feed rather than the e-proceedings feed."
        />
      )}

      <Card title="Details">
        {!row ? (
          <Empty description="No detail available." />
        ) : (
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            size="small"
            bordered
            items={Object.entries(row)
              // Drop noise + nested objects — render only scalar fields.
              .filter(
                ([, v]) =>
                  v !== null &&
                  v !== undefined &&
                  v !== "" &&
                  (typeof v === "string" ||
                    typeof v === "number" ||
                    typeof v === "boolean")
              )
              .map(([k, v]) => ({
                key: k,
                label: k.replace(/_/g, " ").replace(/\b\w/g, (c) =>
                  c.toUpperCase()
                ),
                children:
                  typeof v === "boolean"
                    ? v
                      ? "Yes"
                      : "No"
                    : isLikelyDate(k, v)
                      ? fmtDate(v)
                      : String(v),
              }))}
          />
        )}
      </Card>

      <Paragraph type="secondary" className="!mt-3 text-xs">
        Embedded view - actions like response generation are managed
        through your host application.
      </Paragraph>
    </div>
  );
};

const isLikelyDate = (key, value) => {
  if (typeof value !== "string") return false;
  if (!/date|_at|_on/i.test(key)) return false;
  const d = dayjs(value);
  return d.isValid();
};

export default EmbedNoticeDetail;
